import '../lib/apiConfig'; // Initialize API config
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { supabase } from '../lib/supabase';
import { OpenAPI } from '../app/api/core/OpenAPI';
import { CorporateEntityService } from '../app/api/services/CorporateEntityService';

function ClaimProfile() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [loading, setLoading] = useState(true);
    const [claiming, setClaiming] = useState(false);
    const [error, setError] = useState('');
    const [profile, setProfile] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [selectedUserType, setSelectedUserType] = useState('');

    // Check authentication status and keep in sync with backend
    useEffect(() => {
        const checkAuth = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (session?.user) {
                setIsAuthenticated(true);
                setUser(session.user);
                // Ensure user is synced to backend (e.g. after email verification) so claim API works
                try {
                    const { syncUser } = await import('../lib/authSync');
                    const result = await syncUser();
                    if (result.success && result.data?.userid) {
                        localStorage.setItem('Id', result.data.userid.toString());
                        if (result.data.userType) {
                            localStorage.setItem('userType', result.data.userType);
                        }
                    }
                } catch (e) {
                    console.warn('ClaimProfile: sync user failed', e);
                }
            }
        };
        checkAuth();

        // Listen for auth changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            if (session?.user) {
                setIsAuthenticated(true);
                setUser(session.user);
            } else {
                setIsAuthenticated(false);
                setUser(null);
            }
        });

        return () => subscription.unsubscribe();
    }, []);

    // Load profile information
    useEffect(() => {
        const loadProfile = async () => {
            if (!id || !token) {
                setError('Invalid claim link. Please check the link and try again.');
                setLoading(false);
                return;
            }

            try {
                const numericId = parseInt(id, 10);
                if (Number.isNaN(numericId)) {
                    setError('Invalid profile ID in claim link.');
                    setLoading(false);
                    return;
                }

                const data = await CorporateEntityService.getApiV2CorporateEntityClaimInfo(
                    numericId,
                    token
                );

                if (!data || data.status === false) {
                    setError(data?.error || data?.message || 'Failed to load profile information');
                    setLoading(false);
                    return;
                }

                setProfile(data.data || data);
                setLoading(false);
            } catch (error) {
                console.error('Error loading profile:', error);
                setError('An error occurred while loading profile information. Please try again.');
                setLoading(false);
            }
        };

        loadProfile();
    }, [id, token]);

    const handleGoogleSignup = async () => {
        setError('');
        try {
            const siteUrl = window.location.origin;
            const { error: signInError } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    redirectTo: `${siteUrl}/claim/${id}?token=${token}`,
                },
            });

            if (signInError) {
                setError(signInError.message || 'Failed to sign in with Google');
            }
        } catch (error) {
            console.error('Error signing in with Google:', error);
            setError('An error occurred. Please try again.');
        }
    };

    const handleEmailSignup = async () => {
        // Store claim URL so after magic-link verification we can send user back (Supabase redirects to a simple URL)
        const claimPath = `/claim/${id}?token=${token}`;
        localStorage.setItem('pending_claim', JSON.stringify({ path: claimPath }));
        navigate(`/signup?redirect=${encodeURIComponent(claimPath)}`);
    };

    const handleClaim = async () => {
        if (!isAuthenticated || !user) {
            setError('Please sign in to claim this profile');
            return;
        }

        if (!token) {
            setError('Invalid claim token');
            return;
        }

        if (!selectedUserType || (selectedUserType !== 'BUYER' && selectedUserType !== 'SUPPLIER')) {
            setError('Please select whether you are a Buyer or Supplier');
            return;
        }

        setClaiming(true);
        setError('');

        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.access_token) {
                setError('Not authenticated. Please sign in again.');
                setClaiming(false);
                return;
            }

            const numericId = parseInt(id, 10);
            if (Number.isNaN(numericId)) {
                setError('Invalid profile ID in claim link.');
                setClaiming(false);
                return;
            }

            // Use OpenAPI client for the claim endpoint
            const originalToken = OpenAPI.TOKEN;
            OpenAPI.TOKEN = session.access_token;

            let data;
            try {
                data = await CorporateEntityService.postApiV2CorporateEntityClaim(numericId, {
                    claim_token: token,
                    userType: selectedUserType || undefined
                });
            } finally {
                OpenAPI.TOKEN = originalToken;
            }

            if (!data || data.status === false) {
                setError(data?.error || data?.message || 'Failed to claim profile');
                setClaiming(false);
                return;
            }

            // Sync user so onboarding has latest userType
            const { syncUser } = await import('../lib/authSync');
            const syncResult = await syncUser();
            if (syncResult.success && syncResult.data?.userType) {
                localStorage.setItem('userType', syncResult.data.userType);
            }
            // Store claimed company so onboarding can pre-fill and update instead of create
            localStorage.setItem('company_id', id.toString());
            // Start onboarding from the beginning (no saved step) so claimed data loads
            localStorage.removeItem('onboarding_step');
            localStorage.removeItem('onboarding_draft');

            navigate('/onboarding');
        } catch (error) {
            console.error('Error claiming profile:', error);
            const errorMessage = error.body?.message || error.message || 'An error occurred while claiming the profile';
            setError(errorMessage);
            setClaiming(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-orange mx-auto"></div>
                    <p className="mt-4 text-muted-foreground">Loading profile information...</p>
                </div>
            </div>
        );
    }

    if (error && !profile) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-md w-full space-y-8 bg-card p-8 rounded-lg shadow-md">
                    <div className="text-center">
                        <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                            <svg
                                className="w-8 h-8 text-red-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-foreground mb-2">Error</h2>
                        <p className="text-muted-foreground mb-4">{error}</p>
                        <Button onClick={() => navigate('/')} variant="outline">
                            Go to Home
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl w-full space-y-8 bg-card p-8 rounded-lg shadow-md">
                <div>
                    <h2 className="text-3xl font-bold text-center text-foreground">
                        Claim Your Profile
                    </h2>
                    <p className="mt-2 text-center text-sm text-muted-foreground">
                        Review your profile information and claim it to get started
                    </p>
                </div>

                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                        {error}
                    </div>
                )}

                {/* Profile Preview */}
                {profile && (
                    <div className="space-y-4 border rounded-lg p-6">
                        <h3 className="text-xl font-semibold text-foreground">Profile Information</h3>
                        <div className="space-y-2">
                            <div>
                                <span className="text-sm font-medium text-muted-foreground">Company Name:</span>
                                <p className="text-foreground">{profile.legalname}</p>
                            </div>
                            {profile.email && (
                                <div>
                                    <span className="text-sm font-medium text-muted-foreground">Email:</span>
                                    <p className="text-foreground">{profile.email}</p>
                                </div>
                            )}
                            {profile.website && (
                                <div>
                                    <span className="text-sm font-medium text-muted-foreground">Website:</span>
                                    <p className="text-foreground">{profile.website}</p>
                                </div>
                            )}
                            {profile.phone && (
                                <div>
                                    <span className="text-sm font-medium text-muted-foreground">Phone:</span>
                                    <p className="text-foreground">{profile.phone}</p>
                                </div>
                            )}
                            {profile.description && (
                                <div>
                                    <span className="text-sm font-medium text-muted-foreground">Description:</span>
                                    <p className="text-foreground">{profile.description}</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Authentication Section */}
                {!isAuthenticated ? (
                    <div className="space-y-4">
                        <p className="text-center text-muted-foreground">
                            Please sign in to claim this profile
                        </p>
                        <Button
                            type="button"
                            onClick={handleGoogleSignup}
                            className="w-full"
                            variant="outline"
                        >
                            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                                <path
                                    fill="currentColor"
                                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                />
                                <path
                                    fill="currentColor"
                                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                />
                                <path
                                    fill="currentColor"
                                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                />
                                <path
                                    fill="currentColor"
                                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                />
                            </svg>
                            Sign in with Google
                        </Button>
                        <Button
                            type="button"
                            onClick={handleEmailSignup}
                            className="w-full"
                            variant="outline"
                        >
                            Sign in with Email
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
                            <p className="text-sm">
                                Signed in as: <strong>{user?.email}</strong>
                            </p>
                        </div>
                        <div className="space-y-2">
                            <p className="text-sm font-medium text-foreground">
                                I am claiming this profile as a:
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <button
                                    type="button"
                                    onClick={() => setSelectedUserType('BUYER')}
                                    className={`p-4 text-left border-2 rounded-lg transition-colors ${selectedUserType === 'BUYER'
                                        ? 'border-brand-orange bg-brand-orange-muted'
                                        : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                >
                                    <div className="font-semibold text-foreground">Buyer</div>
                                    <div className="text-sm text-muted-foreground mt-1">
                                        Looking to find suppliers and get matched
                                    </div>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setSelectedUserType('SUPPLIER')}
                                    className={`p-4 text-left border-2 rounded-lg transition-colors ${selectedUserType === 'SUPPLIER'
                                        ? 'border-brand-orange bg-brand-orange-muted'
                                        : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                >
                                    <div className="font-semibold text-foreground">Supplier</div>
                                    <div className="text-sm text-muted-foreground mt-1">
                                        Supplier/manufacturer connecting with buyers
                                    </div>
                                </button>
                            </div>
                        </div>
                        <Button
                            type="button"
                            onClick={handleClaim}
                            disabled={claiming || !selectedUserType}
                            className="w-full"
                        >
                            {claiming ? 'Claiming Profile...' : 'Claim This Profile'}
                        </Button>
                        <p className="text-xs text-muted-foreground text-center">
                            After claiming you will complete onboarding to finish your profile.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ClaimProfile;
